// Shared type definitions for the Bakery Worlds portal.
// Shapes mirror what PrivateWorldsControlService/HttpServerService serialises with Gson.

// ===== User & Authentication =====
export interface DiscordUser {
  id: string;
  username: string;
  discriminator: string;
  avatar: string | null;
}

export interface AuthUser {
  user: DiscordUser;
  minecraftUuid: string | null;
  isStaff: boolean;
  isAdmin: boolean;
  isDeveloper: boolean;
  isVIP?: boolean;
}

/** Staff, admins and developers all get the staff experience. */
export const isStaffUser = (u: AuthUser | null | undefined): boolean =>
  !!u && (u.isStaff || u.isAdmin || u.isDeveloper);

/** Admins and developers only (not plain staff): the per-world Admin tab and PUT /api/worlds/:id/admin. */
export const isAdminUser = (u: AuthUser | null | undefined): boolean =>
  !!u && (u.isAdmin || u.isDeveloper);

/** Server logs are gated to admins and developers. */
export const canViewLogs = (u: AuthUser | null | undefined): boolean => isAdminUser(u);

/** The live Performance tab is gated the same way as the logs. */
export const canViewPerf = (u: AuthUser | null | undefined): boolean => isAdminUser(u);

/** Developers only (not admins or staff): the console command box under the live server log and POST /api/worlds/:id/console. */
export const canRunConsoleCommands = (u: AuthUser | null | undefined): boolean => !!u && u.isDeveloper;

// ===== Worlds =====
export type WorldStatus =
  | 'INACTIVE'
  | 'LOADING'
  | 'RESTORING'
  | 'ACTIVE'
  | 'CREATING'
  | 'ERROR'
  | 'SHUTTING_DOWN'
  | 'OFFLINE';

export type WorldGenerationType = 'VANILLA' | 'FLAT' | 'VOID';

export interface World {
  id: string;
  name: string;
  ownerId: string;
  generationType?: WorldGenerationType;
  status: WorldStatus | string;
  createdAt?: string;
  lastAccessedAt?: string;
  deletedAt?: string;
  deleted?: boolean;
  region?: string;
  restoreFromBackupId?: string;
  seed?: string;
  isTesting?: boolean;
  lastUsedVersion?: string;
  /** Live: when the running server shuts itself down (absent when no timer is armed). */
  shutdownAt?: string;
  shutdownReason?: ShutdownReason;
  /** Admin overrides (see WorldAdminSettings). Only sent to admins / developers. */
  autoShutdownDisabled?: boolean;
  ownerAbsentShutdownDisabled?: boolean;
  /** Highest Y players can build at; absent while the world uses vanilla (319). */
  maxBuildHeight?: number;
  /**
   * The cloud instance the world's server runs on, recorded when it is started and cleared once it has been
   * deleted. Only sent to admins / developers; absent while the world has no server.
   */
  serverId?: string;
  serverIp?: string;
}

export type ShutdownReason = 'EMPTY_SERVER' | 'OWNER_ABSENT' | 'BYPASS';

/** Admin-only per-world settings (PUT /api/worlds/:id/admin accepts any subset). */
export interface WorldAdminSettings {
  /** The server never shuts itself down. */
  autoShutdownDisabled: boolean;
  /** The owner leaving arms no timer; only the last player leaving does. */
  ownerAbsentShutdownDisabled: boolean;
  /** Highest Y players can build at (rounded up by the backend, 1000 -> 1007); applies the next time the world starts. */
  maxBuildHeight: number;
}

/** A player trusted on a world (GET /api/worlds/:id/trusted). `username` is missing when LuckPerms has no record. */
export interface TrustedPlayer {
  uuid: string;
  username?: string | null;
}

export interface WorldSettings {
  worldId: string;
  gravityEnabled: boolean;
  redstoneEnabled: boolean;
  fluidFlowEnabled: boolean;
  allowVisitors: boolean;
}

export interface WorldBackup {
  key: string;
  lastModified: string;
  size: number;
}

/** POST / DELETE /api/worlds/:id/backups/:file/restore. `restartingNow` is only set when marking. */
export interface RestoreMarkResponse {
  success: boolean;
  restoreFromBackupId?: string;
  restartingNow?: boolean;
  world: World;
}

/** File name of a backup (S3 keys are `<worldId>/<file>`). */
export const backupFileName = (key: string | undefined | null): string => (key ? key.split('/').pop() || key : '');

/** True when this backup is the one the world is marked to restore from. */
export const isMarkedForRestore = (world: World, backup: WorldBackup): boolean =>
  !!world.restoreFromBackupId &&
  (world.restoreFromBackupId === backup.key || backupFileName(world.restoreFromBackupId) === backupFileName(backup.key));

export type AuditAction =
  | 'WORLD_CREATED'
  | 'WORLD_LOADED'
  | 'SERVER_OFFLINE'
  | 'SERVER_CRASHED'
  | 'WORLD_BACKUP_CREATED'
  | 'WORLD_BACKUP_REQUESTED'
  | 'WORLD_BACKUP_RESTORED'
  | 'WORLD_DELETED'
  | 'WORLD_RENAMED'
  | 'PLAYER_JOINED'
  | 'PLAYER_LEFT'
  | 'TRUST_ADDED'
  | 'TRUST_REMOVED'
  | 'WORLD_SETTINGS_CHANGED'
  | 'ADMIN_ACTION'
  | 'WARP_CREATED'
  | 'WARP_DELETED'
  | 'WARP_UPDATED';

export interface WorldAudit {
  id: string;
  worldId: string;
  worldName?: string;
  playerId?: string | null;
  action: AuditAction | string;
  details?: string | null;
  timestamp: string;
}

export interface LogEntry {
  timestamp: number;
  message: string;
}

// ===== Crashes (GET /api/worlds/:id/crashes, see control/crash/CrashLogStore.java) =====
export type CrashFileKind = 'LATEST_LOG' | 'CRASH_REPORT' | 'JVM_ERROR';

export interface CrashFile {
  name: string;
  kind: CrashFileKind;
  size: number;
}

/** What a crashed server of the world left behind, copied off it before it was deleted. */
export interface WorldCrash {
  serverId: string;
  /** Epoch seconds the files were copied, just after the exit; also the folder name on the control service. */
  timestamp: number;
  /** The container's exit code; absent when there was none (the box was already down). */
  exitCode?: number;
  files: CrashFile[];
}

// ===== Live world streams (WebSocket /api/ws/worlds, see control/http/ws/StreamFrames.java) =====
export type StreamChannel = 'logs' | 'stats';

export interface StreamLogLine {
  ts: number;
  m: string;
}

/** One sample from scripts/box-stats.sh: bytes, bytes per second, one-decimal percents. */
export interface StatsHost {
  cpuPct: number;
  memUsed: number;
  memTotal: number;
  netRx: number;
  netTx: number;
  netRxTotal: number;
  netTxTotal: number;
  diskRead: number;
  diskWrite: number;
  diskReadTotal: number;
  diskWriteTotal: number;
  diskUsed: number;
  diskTotal: number;
  load1: number;
  ncpu: number;
}

export interface StatsContainer {
  cpuPct: number;
  memUsed: number;
  /** 0 when the container has no memory limit. */
  memLimit: number;
}

export interface StatsSample {
  ts: number;
  host: StatsHost;
  /** null when the container's cgroup could not be read. */
  ct: StatsContainer | null;
}

export type StreamFrame =
  | { type: 'hello'; worldId: string; channel: StreamChannel; ts: number }
  | { type: 'log'; replay: boolean; lines: StreamLogLine[] }
  | { type: 'stats'; replay: boolean; sample: StatsSample }
  | { type: 'ping'; ts: number }
  | { type: 'error'; code: string; message: string; permanent: boolean }
  | { type: 'end'; reason: string; exit: number };

// ===== Dashboard =====
export interface DashboardData {
  isVIP: boolean;
  isStaff: boolean;
  stats: {
    worldCount: number;
    backupCount: number;
    recentActivity: WorldAudit[];
  };
}

export interface StaffDashboardData {
  stats: {
    totalWorlds: number;
    loadedWorldCount: number;
    totalUsers: number;
    totalBackupCount: number;
    recentWorldActivity: WorldAudit[];
  };
}

// ===== Feedback =====
export type FeedbackType = 'bug' | 'feature' | 'improvement' | 'other';

export interface FeedbackData {
  type: FeedbackType;
  title: string;
  description: string;
  stepsToReproduce?: string;
  expectedBehavior?: string;
  actualBehavior?: string;
  worldId?: string;
}

// ===== Plots (PlotSquared plots on the plot servers; shapes come from BakeryPlotSquaredSnapshots via PlotsApi.java) =====
/** Inclusive block box of one plot region, [x, y, z] corners. A merged plot has several. */
export interface PlotRegion {
  min: number[];
  max: number[];
}

/** One owned base plot. `plot` is the id used in API paths ("12_-3"), `plotId` PlotSquared's display id ("12;-3"). */
export interface Plot {
  world: string;
  plot: string;
  plotId: string;
  regions: PlotRegion[];
}

/** One plot size on the "Your plots" page (GET /api/plots). `error` is set when that plot server could not be asked. */
export interface PlotSizeGroup {
  size: string;
  plots: Plot[];
  error?: string | null;
}

/** What identifies a plot in API paths and URLs. */
export interface PlotRef {
  size: string;
  world: string;
  plot: string;
}

/** A player on a plot's trusted or added list. `name` is "*" for everyone, missing when it could not be resolved. */
export interface PlotMember {
  uuid: string;
  name?: string | null;
}

/** The two lists a plot owner manages: /plot trust and /plot add. */
export type PlotMemberList = 'trusted' | 'members';

/** A flag the plot sets itself, as "/plot flag set <name> <value>" would show it. */
export interface PlotFlag {
  name: string;
  value: string;
}

/** A plot snapshot; `createdAt` (epoch millis) is its id. */
export interface PlotSnapshot {
  createdAt: number;
  size: number;
}

/** POST /api/plots/.../snapshots. `created` is false when the plot was unchanged and the latest snapshot was kept. */
export interface PlotSnapshotCreated {
  createdAt: number;
  created: boolean;
}

// ===== UI =====
export type ServerAction = 'start' | 'stop' | 'kill';
export type WorldTab = 'settings' | 'backups' | 'players' | 'activity' | 'logs' | 'perf' | 'admin' | 'map';
export type PlotTab = 'members' | 'flags' | 'snapshots';
